import React, { useEffect, useState } from "react";
import { collection, getDocs, query, orderBy } from "firebase/firestore";
import { db } from "../firebase";
import { Link } from "react-router-dom";
import PageWrapper from "../components/PageWrapper";
import "./css/Blogs.css";
import { AiOutlineEye } from "react-icons/ai";
import { BiMessageRoundedDetail } from "react-icons/bi";
import { Helmet } from "react-helmet";

const Blogs = () => {
  const [articles, setArticles] = useState([]);
  const categoryIcons = {
    "News & Updates": "📰",
    "Guides & Tutorials": "📘",
    "Lore & Theories": "🧠",
    "Ranked Lists & Opinions": "📊",
    "Fan Content & Creativity": "🎨",
    "Game Reviews & Retrospectives": "🎮",
    "GTA RP Coverage": "🎭",
    "Community Spotlight": "🌟",
    "Mods & Technical Help": "🛠️",
    "Opinion & Editorials": "✍️",
  };

  const getCategoryIcon = (category) => categoryIcons[category] || "📄";

  useEffect(() => {
    const fetchArticles = async () => {
      const q = query(collection(db, "articles"), orderBy("timestamp", "desc"));
      const snap = await getDocs(q);
      const data = snap.docs.map((doc) => ({ id: doc.id, ...doc.data() }));
      setArticles(data);
    };

    fetchArticles();
  }, []);

  return (
    <>
      <Helmet>
        <title>
          GTAFanPage.com – GTA 6 Blog, News, Leaks & Community Updates
        </title>
        <meta
          name="description"
          content="Explore the latest GTA 6 articles, leaks, fan theories, and Rockstar updates. Join the GTAFanPage blog community and stay informed."
        />
        <link rel="canonical" href="https://www.gtafanpage.com/blogs" />

        {/* Open Graph / Facebook */}
        <meta property="og:type" content="website" />
        <meta
          property="og:title"
          content="GTA 6 Blog | Latest News, Leaks & Community Discussions"
        />
        <meta
          property="og:description"
          content="Read in-depth GTA VI articles and join the fan discussion on GTAFanPage.com. Covering leaks, lore, updates, and more."
        />
        <meta property="og:url" content="https://www.gtafanpage.com/blogs" />
        <meta
          property="og:image"
          content="https://www.gtafanpage.com/og-image.jpg"
        />

        {/* Twitter */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta
          name="twitter:title"
          content="GTAFanPage Blog | Grand Theft Auto 6 News & Updates"
        />
        <meta
          name="twitter:description"
          content="Browse GTA VI fan content, developer rumors, guides, and insights on GTAFanPage.com – a fan community for Rockstar lovers."
        />
        <meta
          name="twitter:image"
          content="https://www.gtafanpage.com/og-image.jpg"
        />

        {/* JSON-LD Schema for Blog Listing */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Blog",
            name: "GTAFanPage Blog",
            url: "https://www.gtafanpage.com/blogs",
            description:
              "Latest blog posts and articles about Grand Theft Auto VI, including news, rumors, fan theories, and gameplay discussion.",
            publisher: {
              "@type": "Organization",
              name: "GTAFanPage",
              url: "https://www.gtafanpage.com",
              logo: {
                "@type": "ImageObject",
                url: "https://www.gtafanpage.com/logo192.png",
              },
            },
          })}
        </script>
      </Helmet>

      <PageWrapper>
        <div className="contact-page-wrapper">
          <div className="forum-style-list">
            {articles.map((article) => (
              <div key={article.id} className="forum-thread-row">
                <div className="left-section">
                  <div className="thread-info">
                    <div className="thread-title">
                      <span className="forum-icon-inline">
                        {getCategoryIcon(article.category)}
                      </span>
                      <Link to={`/articles/${article.id}`}>
                        {article.title}
                      </Link>
                    </div>

                    <div className="thread-meta">
                      {article.category} · {article.authorName || "Unknown"} ·{" "}
                      {article.timestamp?.toDate().toLocaleDateString("en-GB", {
                        day: "numeric",
                        month: "short",
                        year: "numeric",
                      })}
                    </div>
                    <div className="thread-description">
                      {
                        article.content
                          ?.replace(/[#>*`~]/g, "") // Remove markdown symbols
                          ?.split("\n") // Split into lines
                          ?.find((line) => line.trim().length > 0) // Pick the first non-empty one
                      }
                    </div>
                    <div className="tags">
                      {article.tags?.map((tag) => (
                        <span key={tag} className="tag">
                          #{tag}
                        </span>
                      ))}
                    </div>
                  </div>
                </div>

                <div className="right-section">
                  <div className="thread-stats">
                    <span className="stat">
                      <AiOutlineEye
                        style={{ verticalAlign: "middle", marginRight: 4 }}
                      />
                      {article.views || 0}
                    </span>
                    <span className="stat">
                      <BiMessageRoundedDetail
                        style={{ verticalAlign: "middle", marginRight: 4 }}
                      />
                      {article.reactions?.comment || 0}
                    </span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </PageWrapper>
    </>
  );
};

export default Blogs;
